/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Slide,
  Tab,
  Tabs,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  Avatar
} from '@mui/material';
import {
  ExpandMore,
  Article as ArticleIcon,
  Person,
  School as SchoolIcon,
  Close,
  Info,
  CheckCircle,
  NavigateNext,
  Visibility,
  Edit,
  Description,
  Link as LinkIcon,
  DateRange,
  MenuBook,
  Science,
  Bookmark,
  PictureAsPdf,
  LibraryBooks,
  ConfirmationNumber,
  PublishedWithChangesRounded
} from '@mui/icons-material';
import { getTodaysDate } from '../../utilities/Functions';
import GuestUser from '../main/GuestUser';
import NavigationMenu from '../navigation/NavigationMenu';
import { keyframes } from '@emotion/react';
import { AUTH_TOKEN, BASE_URL, headers } from '../../utilities/Env';
import { APIPublishedWorkStore, APIPublishedWorkUpdate, APIResearcherList } from '../../utilities/APIS';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const fadeIn = keyframes`
from { opacity: 0; transform: translateY(20px); }
to { opacity: 1; transform: translateY(0); }
`;

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  borderRadius: '16px',
  boxShadow: theme.shadows[3],
  animation: `${fadeIn} 0.5s ease-out`,
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.light,
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main + '08',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px 12px 0 0',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

// PDF Viewer Component
const PDFViewer = ({ open, onClose, pdfUrl, title = "PDF Document" }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '70vh' }}
            title={title}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const SubmitPublished = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [researchersData, setResearchersData] = useState([]);
  const [postStatus, setPostStatus] = useState('ADD');
  const [editingId, setEditingId] = useState(null);
  const showToast = useCustomToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [userData, setUserData] = useState(null);

  // Publication data state
  const [publicationData, setPublicationData] = useState({
    title: '',
    authors: '',
    publication_year: new Date().getFullYear(),
    publisher: '',
    url: '',
    doi: '',
    issn: '',
    volume: '',
    issue: '',
    pages: '',
    abstract: '',
    publication_type: 'journal',
    status: 'published',
    publication_date: '',
  });

  const [state, setState] = useState({
    showSubmitBtn: true,
    showContinueBtn: false,
    showPostingAnimation: false,
    fileName: null,
    fileSize: null,
    pdfFilePreview: null,
    userLoggedIn: false,
    userName: '',
    userId: '',
  });

  useEffect(() => {
    loadUser();
    fetchData();
    checkEditMode();
  }, []);

  const checkEditMode = () => {
    if (location.state) {
      const data = location.state;
      setPostStatus('UPDATE');
      
      if (data.id) {
        setEditingId(data.id);
      }

      setPublicationData({
        title: data.title || '',
        authors: data.author_name || data.authors || '',
        publication_year: data.publication_year || new Date().getFullYear(),
        publisher: data.publisher || '',
        url: data.url || '',
        doi: data.doi || '',
        issn: data.issn || '',
        volume: data.volume || '',
        issue: data.issue || '',
        pages: data.pages || '',
        abstract: data.abstract || '',
        publication_type: data.publication_type || 'journal',
        status: data.status || 'published',
        publication_date: data.publication_date || '',
      });

      setState(prev => ({
        ...prev,
        fileName: data.file_name ? data.file_name.split('/').pop() : '',
        pdfFilePreview: data.file_name ? `${BASE_URL}${data.file_name}` : null,
      }));

      showToast("Edit Mode", "You are now editing an existing publication", "info");
    }
  };

  const loadUser = () => {
    const user = sessionStorage.getItem('reseacher');
    if (user) {
      const jsonData = JSON.parse(user);
      setUserData(jsonData);
      setState(prev => ({
        ...prev,
        userName: jsonData.name,
        userLoggedIn: jsonData.loggedIn,
        userId: jsonData.id,
      }));
      
      // Set author name from logged-in user only if not in edit mode
      if (!location.state) {
        setPublicationData(prev => ({
          ...prev,
          authors: jsonData.name,
        }));
      }
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const researchersRes = await axios.get(APIResearcherList, { headers });
      const researchers = Array.isArray(researchersRes.data.data) ? researchersRes.data.data.reverse() : [];
      setResearchersData(researchers);
    } catch (error) {
      let errorMessage = "Failed to load data";
      if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
      }
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setPublicationData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setState(prev => ({
        ...prev,
        fileName: file,
        fileSize: file.size,
        pdfFilePreview: URL.createObjectURL(file)
      }));
      showToast("File Selected", `${file.name} selected successfully`, "success");
    } else if (file) {
      showToast("File Error", "Please select a valid PDF file", "error");
    }
  };

  const handlePreviewPdf = () => {
    if (state.pdfFilePreview) {
      setSelectedPdfUrl(state.pdfFilePreview);
      setPdfDialogOpen(true);
    }
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setSelectedPdfUrl('');
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!publicationData.title.trim()) {
      showToast("Required Field", "Please enter the publication title", "warning");
      return;
    }
    if (!publicationData.publisher.trim()) {
      showToast("Required Field", "Please enter the publisher name", "warning");
      return;
    }
    if (!publicationData.publication_year) {
      showToast("Required Field", "Please select the publication year", "warning");
      return;
    }

    setState(prev => ({ ...prev, showPostingAnimation: true }));

    const formDataToSend = new FormData();
    formDataToSend.append('author_id', state.userId);
    formDataToSend.append('author_name', publicationData.authors);
    formDataToSend.append('publication_year', publicationData.publication_year);
    formDataToSend.append('title', publicationData.title);
    formDataToSend.append('publisher', publicationData.publisher);
    formDataToSend.append('url', publicationData.url || '');
    formDataToSend.append('doi', publicationData.doi || '');
    formDataToSend.append('issn', publicationData.issn || '');
    formDataToSend.append('volume', publicationData.volume || '');
    formDataToSend.append('issue', publicationData.issue || '');
    formDataToSend.append('pages', publicationData.pages || '');
    formDataToSend.append('abstract', publicationData.abstract || '');
    formDataToSend.append('publication_type', publicationData.publication_type);
    formDataToSend.append('status', publicationData.status);
    formDataToSend.append('publication_date', publicationData.publication_date || '');

    if (state.fileName && typeof state.fileName !== 'string') {
      formDataToSend.append('file_name', state.fileName);
    }

    if (postStatus === "UPDATE" && editingId) {
      formDataToSend.append('update_id', editingId);
    }

    try {
      let postRequest;
      if (postStatus === "ADD") {
        postRequest = await axios.post(APIPublishedWorkStore, formDataToSend, { headers });
      } else {
        postRequest = await axios.post(APIPublishedWorkUpdate, formDataToSend, { headers });
      }

      const response = postRequest.data;
      if (response.status === "success") {
        setState(prev => ({
          ...prev,
          showContinueBtn: true,
          showPostingAnimation: false,
          showSubmitBtn: false
        }));
        
        const successMessage = postStatus === "ADD" 
          ? "Publication submitted successfully!" 
          : "Publication updated successfully!";
          
        showToast("Success", successMessage, "success");
        
        // Reset form after 3 seconds for ADD mode
        if (postStatus === "ADD") {
          setTimeout(() => {
            resetForm();
            setActiveStep(0);
          }, 3000);
        }
      } else {
        showToast("Error", response.message || "Submission failed", "error");
        setState(prev => ({ ...prev, showPostingAnimation: false }));
      }
    } catch (error) {
      console.error('Submission error:', error);
      if (error.response) {
        if (error.response.status === 422) {
          const errors = error.response.data.errors;
          let errorMessages = [];
          for (const field in errors) {
            errorMessages.push(...errors[field]);
          }
          showToast("Validation Error", errorMessages.join("\n"), "error");
        } else {
          showToast("Error", error.response.data?.message || "An error occurred", "error");
        }
      } else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
      } else {
        showToast("Error", "An unexpected error occurred", "error");
      }
      setState(prev => ({ ...prev, showPostingAnimation: false }));
    }
  };

  const resetForm = () => {
    setPublicationData({
      title: '',
      authors: userData?.name || '',
      publication_year: new Date().getFullYear(),
      publisher: '',
      url: '',
      doi: '',
      issn: '',
      volume: '',
      issue: '',
      pages: '',
      abstract: '',
      publication_type: 'journal',
      status: 'published',
      publication_date: '',
    });
    setState(prev => ({
      ...prev,
      fileName: null,
      fileSize: null,
      pdfFilePreview: null,
      showSubmitBtn: true,
      showContinueBtn: false,
    }));
  };

  const getPublicationTypeIcon = (type) => {
    switch(type) {
      case 'journal': return <MenuBook />;
      case 'conference': return <Science />;
      case 'book': return <LibraryBooks />;
      case 'chapter': return <Bookmark />;
      case 'thesis': return <SchoolIcon />;
      default: return <ArticleIcon />;
    }
  };

  const renderBasicInfoTab = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Publication Guidelines:</strong> Please provide accurate information about your publication.
          Fields marked with * are required.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Publication Title *"
            value={publicationData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder="Enter the full title of your publication"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Author(s) *"
            value={publicationData.authors}
            onChange={(e) => handleFieldChange('authors', e.target.value)}
            placeholder="Enter author names"
            variant="outlined"
            helperText="Format: Last Name, First Initial (e.g., Smith, J.)"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="number"
            label="Publication Year *"
            value={publicationData.publication_year}
            onChange={(e) => handleFieldChange('publication_year', parseInt(e.target.value) || new Date().getFullYear())}
            variant="outlined"
            inputProps={{ min: 1900, max: new Date().getFullYear() + 5 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Publisher *"
            value={publicationData.publisher}
            onChange={(e) => handleFieldChange('publisher', e.target.value)}
            placeholder="Enter publisher name"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="URL"
            value={publicationData.url}
            onChange={(e) => handleFieldChange('url', e.target.value)}
            placeholder="https://..."
            variant="outlined"
            InputProps={{
              startAdornment: <LinkIcon color="primary" sx={{ mr: 1 }} />
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Publication Type *</InputLabel>
            <Select
              value={publicationData.publication_type}
              onChange={(e) => handleFieldChange('publication_type', e.target.value)}
              label="Publication Type *"
            >
              <MenuItem value="journal">Journal Article</MenuItem>
              <MenuItem value="conference">Conference Paper</MenuItem>
              <MenuItem value="book">Book</MenuItem>
              <MenuItem value="chapter">Book Chapter</MenuItem>
              <MenuItem value="thesis">Thesis/Dissertation</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status *</InputLabel>
            <Select
              value={publicationData.status}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              label="Status *"
            >
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="in_press">In Press</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Publication Date"
            value={publicationData.publication_date}
            onChange={(e) => handleFieldChange('publication_date', e.target.value)}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderBibliographicInfoTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="DOI"
          value={publicationData.doi}
          onChange={(e) => handleFieldChange('doi', e.target.value)}
          placeholder="10.xxxx/xxxxx"
          variant="outlined"
          helperText="Digital Object Identifier"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="ISSN/ISBN"
          value={publicationData.issn}
          onChange={(e) => handleFieldChange('issn', e.target.value)}
          placeholder="ISSN or ISBN number"
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Volume"
          value={publicationData.volume}
          onChange={(e) => handleFieldChange('volume', e.target.value)}
          placeholder="Volume number"
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Issue"
          value={publicationData.issue}
          onChange={(e) => handleFieldChange('issue', e.target.value)}
          placeholder="Issue number"
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Pages"
          value={publicationData.pages}
          onChange={(e) => handleFieldChange('pages', e.target.value)}
          placeholder="Page range (e.g., 1-15)"
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Abstract"
          value={publicationData.abstract}
          onChange={(e) => handleFieldChange('abstract', e.target.value)}
          placeholder="Provide a brief summary of your publication..."
          variant="outlined"
        />
      </Grid>
    </Grid>
  );

  const renderAttachmentsTab = () => (
    <Box>
      <SectionCard>
        <SectionHeader>
          <PictureAsPdf color="primary" />
          <Typography variant="h6" color="primary" fontWeight="600">
            PDF Attachment
          </Typography>
        </SectionHeader>
        <SectionContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Attach your publication PDF file (optional)
            </Typography>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="pdf-upload"
            />
            <label htmlFor="pdf-upload">
              <Button 
                variant="contained" 
                component="span"
                color="primary"
                startIcon={<PictureAsPdf />}
              >
                Upload PDF
              </Button>
            </label>
            {state.fileName && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  Selected: {typeof state.fileName === 'string' ? state.fileName : state.fileName.name}
                </Typography>
                {state.pdfFilePreview && (
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={handlePreviewPdf}
                  >
                    Preview
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </SectionContent>
      </SectionCard>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Uploading a PDF allows other researchers to access your publication.
          Please ensure you have the rights to share this document.
        </Typography>
      </Alert>
    </Box>
  );

  const renderPreviewTab = () => (
    <StyledCard>
      <CardContent>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" color="primary" gutterBottom fontWeight="bold">
              {publicationData.title || 'Untitled Publication'}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {publicationData.authors || 'Author name not specified'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Chip 
                icon={getPublicationTypeIcon(publicationData.publication_type)}
                label={publicationData.publication_type.charAt(0).toUpperCase() + publicationData.publication_type.slice(1)}
                color="primary"
                variant="outlined"
              />
              <Chip 
                icon={<DateRange />}
                label={`Year: ${publicationData.publication_year}`}
                variant="outlined"
              />
              <Chip 
                icon={<PublishedWithChangesRounded />}
                label={`Publisher: ${publicationData.publisher || 'Not specified'}`}
                variant="outlined"
              />
              <Chip 
                icon={<ConfirmationNumber />}
                label={publicationData.status}
                color={publicationData.status === 'published' ? 'success' : 'warning'}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Bibliographic Information */}
          {(publicationData.doi || publicationData.issn || publicationData.volume || publicationData.issue || publicationData.pages || publicationData.url) && (
            <SectionCard>
              <SectionHeader>
                <Description color="primary" />
                <Typography variant="h6" color="primary" fontWeight="600">
                  Bibliographic Information
                </Typography>
              </SectionHeader>
              <SectionContent>
                <Grid container spacing={2}>
                  {publicationData.doi && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>DOI:</strong> {publicationData.doi}
                      </Typography>
                    </Grid>
                  )}
                  {publicationData.issn && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>ISSN/ISBN:</strong> {publicationData.issn}
                      </Typography>
                    </Grid>
                  )}
                  {publicationData.volume && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Volume:</strong> {publicationData.volume}
                      </Typography>
                    </Grid>
                  )}
                  {publicationData.issue && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Issue:</strong> {publicationData.issue}
                      </Typography>
                    </Grid>
                  )}
                  {publicationData.pages && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Pages:</strong> {publicationData.pages}
                      </Typography>
                    </Grid>
                  )}
                  {publicationData.url && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>URL:</strong>{' '}
                        <a href={publicationData.url} target="_blank" rel="noopener noreferrer">
                          {publicationData.url}
                        </a>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </SectionContent>
            </SectionCard>
          )}

          {/* Abstract */}
          {publicationData.abstract && (
            <SectionCard>
              <SectionHeader>
                <MenuBook color="primary" />
                <Typography variant="h6" color="primary" fontWeight="600">
                  Abstract
                </Typography>
              </SectionHeader>
              <SectionContent>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {publicationData.abstract}
                </Typography>
              </SectionContent>
            </SectionCard>
          )}

          {/* PDF Attachment */}
          {state.pdfFilePreview && (
            <SectionCard>
              <SectionHeader>
                <PictureAsPdf color="primary" />
                <Typography variant="h6" color="primary" fontWeight="600">
                  PDF Document
                </Typography>
              </SectionHeader>
              <SectionContent>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={handlePreviewPdf}
                >
                  Preview PDF
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {typeof state.fileName === 'string' ? state.fileName : state.fileName?.name}
                </Typography>
              </SectionContent>
            </SectionCard>
          )}

          {/* Show message if no data entered yet */}
          {!publicationData.title && !publicationData.abstract && !state.pdfFilePreview && (
            <Alert severity="info">
              Complete the previous steps to see your publication preview here.
            </Alert>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );

  const steps = [
    { label: 'Basic Information', icon: <ArticleIcon />, content: renderBasicInfoTab() },
    { label: 'Bibliographic Info', icon: <Description />, content: renderBibliographicInfoTab() },
    { label: 'Attachments', icon: <PictureAsPdf />, content: renderAttachmentsTab() },
    { label: 'Preview', icon: <Visibility />, content: renderPreviewTab() },
  ];

  if (loading) {
    return (
      <>
        <NavigationMenu />
        <LoadingState />
      </>
    );
  }

  if (!state.userLoggedIn) {
    return (
      <>
        <NavigationMenu />
        <GuestUser />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <NavigationMenu />
      <Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
        {/* Header */}
        <Slide direction="down" in={true} mountOnEnter unmountOnExit>
          <Box sx={{
            mb: 3,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {postStatus === 'UPDATE' ? 'Edit Publication' : 'Submit Publication'}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {postStatus === 'UPDATE' ? 'Update your publication details' : 'Share your academic work with the community'}
                </Typography>
                {postStatus === 'UPDATE' && (
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Editing ID: {editingId}
                  </Typography>
                )}
              </Box>
              <Button
                component={Link}
                to="/my-publications"
                variant="contained"
                color="secondary"
                startIcon={<LibraryBooks />}
              >
                My Publications
              </Button>
            </Box>
          </Box>
        </Slide>

        {/* Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel StepIconComponent={() => (
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar sx={{ 
                      bgcolor: activeStep >= index ? theme.palette.primary.main : theme.palette.grey[300],
                      color: 'white',
                      mx: 'auto',
                      mb: 1,
                      width: 40,
                      height: 40
                    }}>
                      {step.icon}
                    </Avatar>
                    <Typography variant="caption">{step.label}</Typography>
                  </Box>
                )}>
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <StyledCard>
          <CardContent>
            {steps[activeStep].content}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(prev => prev - 1)}
                disabled={activeStep === 0}
              >
                Previous
              </Button>
              {activeStep === steps.length - 1 ? (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSubmit}
                    disabled={state.showPostingAnimation}
                    startIcon={state.showPostingAnimation ? <CircularProgress size={20} /> : <CheckCircle />}
                  >
                    {state.showPostingAnimation ? 'Processing...' : (postStatus === 'UPDATE' ? 'Update Publication' : 'Submit Publication')}
                  </Button>
                  {state.showContinueBtn && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate("/my-publications")}
                    >
                      View My Publications
                    </Button>
                  )}
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(prev => prev + 1)}
                >
                  Next
                </Button>
              )}
            </Box>

            {/* Loading animation */}
            {state.showPostingAnimation && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                  {postStatus === 'UPDATE' ? 'Updating publication...' : 'Submitting publication...'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Container>

      {/* PDF Viewer Dialog */}
      <PDFViewer
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        pdfUrl={selectedPdfUrl}
        title="Publication PDF Preview"
      />
    </Box>
  );
};

export default SubmitPublished;